import Empty from 'constants/empty';
import saveForLaterApi from 'services/api/basket/saveForLater';
import Actions from 'actions/Actions';
import RwdBasketActions from 'actions/RwdBasketActions/RwdBasketActions';
import AddToBasketActions from 'actions/AddToBasketActions';
import basketConstants from 'constants/Basket';
import { DELIVERY_METHOD_TYPES } from 'constants/RwdBasket';
import {
    SAVE_FOR_LATER_LIST_TYPE, DEFAULT_SHOPPING_LIST_NAME, SAVE_FOR_LATER_MAX_ITEMS, ACTION_TYPES
} from 'constants/saveForLater';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SavedForLaterCarouselEN from 'components/SaveForLater/SavedForLaterCarousel/locales/SavedForLaterCarousel_en_US';
import SavedForLaterCarouselFR from 'components/SaveForLater/SavedForLaterCarousel/locales/SavedForLaterCarousel_fr_CA';
import basketUtils from 'utils/Basket';
import {
    triggerSaveForLaterLinkEvent,
    triggerSaveForLaterPageLoad,
    SFL_ACTIONS,
    SFL_CONTEXTS
} from 'analytics/bindingMethods/pages/basket/saveForLaterBindings';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(SavedForLaterCarouselEN, SavedForLaterCarouselFR);

const omitUndefined = (obj = Empty.Object) => {
    return Object.keys(obj).reduce((result, key) => {
        if (obj[key] !== undefined) {
            result[key] = obj[key];
        }

        return result;
    }, {});
};

export { ACTION_TYPES };

function setLoading(loading) {
    return { type: ACTION_TYPES.SET_SAVE_FOR_LATER_LOADING, loading };
}

function updateLists(lists) {
    return { type: ACTION_TYPES.UPDATE_SAVE_FOR_LATER_LISTS, lists };
}

function updateItems(items) {
    return { type: ACTION_TYPES.UPDATE_SAVE_FOR_LATER_ITEMS, items };
}

function addItemOptimistic(item) {
    return { type: ACTION_TYPES.ADD_SAVE_FOR_LATER_ITEM_OPTIMISTIC, item };
}

function removeItemOptimistic(skuId) {
    return { type: ACTION_TYPES.REMOVE_SAVE_FOR_LATER_ITEM_OPTIMISTIC, skuId };
}

function setMovingSku(skuId) {
    return { type: ACTION_TYPES.SET_MOVING_SKU, skuId };
}

function removeMovingSku(skuId) {
    return { type: ACTION_TYPES.REMOVE_MOVING_SKU, skuId };
}

function clearMovingSkus() {
    return { type: ACTION_TYPES.CLEAR_MOVING_SKUS };
}

function setConfirmation(confirmationData) {
    return { type: ACTION_TYPES.SET_SAVE_FOR_LATER_CONFIRMATION, confirmationData };
}

function clearConfirmation() {
    return { type: ACTION_TYPES.CLEAR_SAVE_FOR_LATER_CONFIRMATION };
}

function setBasketConfirmation(confirmationData) {
    return { type: ACTION_TYPES.SET_BASKET_CONFIRMATION, confirmationData };
}

function clearBasketConfirmation() {
    return { type: ACTION_TYPES.CLEAR_BASKET_CONFIRMATION };
}

/**
 * Show a generic error modal to inform the user of a failure
 * @param {Object} error - Error object from API
 * @param {Object} [options] - Optional customization options
 * @param {string} [options.title] - Custom title (defaults to 'Error')
 * @param {string} [options.message] - Custom message (overrides error.errorMessages)
 * @param {string} [options.buttonText] - Custom button text (defaults to 'Got It')
 * @param {string} [options.buttonWidth] - Custom button width (defaults to '50%')
 * @returns {Object} Redux action
 */
function showGenericErrorModal(error, options = Empty.Object) {
    const errorMessage = options.message || error?.errorMessages?.[0] || getText('genericErrorMessage');
    const title = options.title || getText('genericErrorTitle');
    const buttonText = options.buttonText || getText('gotIt');
    const buttonWidth = options.buttonWidth || '50%';

    return Actions.showInfoModal({
        isOpen: true,
        title,
        message: errorMessage,
        buttonText,
        buttonWidth,
        footerDisplay: 'flex',
        footerJustifyContent: 'end'
    });
}

/**
 * Fetch lists for a profile.
 * @param {string|number} profileId
 * @param {object} options optional query params
 * @param {function} cbSuccess optional success callback
 * @param {function} cbError optional error callback
 */
function getSaveForLaterLists(profileId, options = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        dispatch(setLoading(true));

        return saveForLaterApi
            .getLists(profileId, options)
            .then(listsResponse => {
                const hasListId = listsResponse?.shoppingListId || listsResponse?.id;
                const lists = hasListId ? [listsResponse] : Empty.Array;
                dispatch(updateLists(lists));

                const items = listsResponse?.shoppingListItems || Empty.Array;
                dispatch(updateItems(items));

                dispatch(setLoading(false));

                if (typeof cbSuccess === 'function') {
                    cbSuccess(listsResponse);
                }

                return listsResponse;
            })
            .catch(error => {
                dispatch(setLoading(false));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Create a new list.
 */
function createSaveForLaterList(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        dispatch(setLoading(true));

        const normalizedPayload = omitUndefined({
            shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase(),
            shoppingListName: payload.shoppingListName || DEFAULT_SHOPPING_LIST_NAME,
            isDefault: payload.isDefault
        });

        return saveForLaterApi
            .createList(profileId, normalizedPayload)
            .then(createdList => {
                // After creation, refetch lists for carousel rendering
                return dispatch(getSaveForLaterLists(profileId)).then(() => {
                    if (typeof cbSuccess === 'function') {
                        cbSuccess(createdList);
                    }

                    return createdList;
                });
            })
            .catch(error => {
                dispatch(setLoading(false));

                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Create an item in a list. payload should minimally contain { skuId, shoppingListName OR shoppingListId }.
 * Low-level API wrapper - use addItemToSaveForLater for high-level orchestration.
 */
function createSaveForLaterItem(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return dispatch => {
        // Optimistic state update (optional – requires reducer support)
        if (payload?.skuId) {
            dispatch(addItemOptimistic(payload));
        }

        const normalizedPayload = omitUndefined({
            source: payload.source || 'productPage',
            productId: payload.productId,
            skuId: payload.skuId,
            shoppingListId: payload.shoppingListId,
            shoppingListType: (payload.shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase()
        });

        return saveForLaterApi
            .addItem(profileId, normalizedPayload)
            .then(addItemResponse => {
                // Refresh lists for carousel rendering
                return dispatch(getSaveForLaterLists(profileId)).then(() => {
                    if (typeof cbSuccess === 'function') {
                        cbSuccess(addItemResponse);
                    }

                    return addItemResponse;
                });
            })
            .catch(error => {
                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Delete an item from a Save For Later list.
 * Low-level API wrapper - use removeItemFromSaveForLater for high-level orchestration.
 */
function deleteSaveForLaterItem(profileId, payload = Empty.Object, cbSuccess, cbError) {
    return () => {
        const { skuId, productId, shoppingListId, shoppingListType } = payload || Empty.Object;

        if (!skuId || !productId || !shoppingListId) {
            const error = new Error('deleteSaveForLaterItem requires skuId, productId, and shoppingListId.');

            return Promise.reject(error);
        }

        const normalizedPayload = omitUndefined({
            skuId,
            productId,
            shoppingListId,
            shoppingListType: (shoppingListType || SAVE_FOR_LATER_LIST_TYPE).toLowerCase()
        });

        return saveForLaterApi
            .removeItem(profileId, normalizedPayload)
            .then(removeItemResponse => {
                if (typeof cbSuccess === 'function') {
                    cbSuccess(removeItemResponse);
                }

                return removeItemResponse;
            })
            .catch(error => {
                if (typeof cbError === 'function') {
                    cbError(error);
                }

                return Promise.reject(error);
            });
    };
}

/**
 * Ensure a Save For Later list exists for the user.
 * Returns the listId, creating a new list if necessary.
 *
 * @param {string|number} profileId - User profile ID
 * @returns {Promise<string>} - The list ID
 */
function ensureSaveForLaterListExists(profileId) {
    return (dispatch, getState) => {
        const state = getState();
        const existingListId = state.saveForLater?.listId;
        const existingLists = state.saveForLater?.lists || Empty.Array;

        // If we already have a listId in Redux, use it
        if (existingListId) {
            return Promise.resolve(existingListId);
        }

        // If we have lists cached but no listId, check if we fetched recently
        if (existingLists.length > 0) {
            // Lists exist but no listId - try to extract it from the first list
            const fallbackListId = existingLists[0]?.shoppingListId || existingLists[0]?.id;

            if (fallbackListId) {
                return Promise.resolve(fallbackListId);
            }
        }

        // Otherwise, fetch lists with full product details to find or create one
        return dispatch(getSaveForLaterLists(profileId)).then(listsResponse => {
            // Extract listId from the API response (single list object)
            const extractedListId = listsResponse?.shoppingListId || listsResponse?.id;

            if (extractedListId) {
                return extractedListId;
            }

            // Only create a new list if we confirmed the backend has no list
            return dispatch(
                createSaveForLaterList(profileId, {
                    shoppingListType: SAVE_FOR_LATER_LIST_TYPE,
                    shoppingListName: DEFAULT_SHOPPING_LIST_NAME,
                    isDefault: true
                })
            ).then(createdList => {
                // Extract listId from the creation response
                const newListId = createdList?.shoppingListId || createdList?.id;

                if (!newListId) {
                    throw new Error('Failed to create Save For Later list');
                }

                return newListId;
            });
        });
    };
}

/**
 * Add an item to Save For Later with full orchestration:
 * - Handles signed-out users (shows sign-in modal)
 * - Checks for duplicates to prevent adding items already in the list
 * - Ensures list exists
 * - Optimistic updates
 * - Error handling
 * - Basket refresh after sign-in
 *
 * @param {object} params - { skuId, productId, item, basketType }
 * @returns {Promise|null} - Returns null for signed-out flow, Promise with status for signed-in
 *                           Status can be 'NEWLY_SAVED' or 'ALREADY_SAVED'
 */
function addItemToSaveForLater({ skuId, productId, item, basketType }) {
    return (dispatch, getState) => {
        const state = getState();
        const profileId = state.user?.profileId;

        // Handle signed-out users
        if (!profileId) {
            dispatch(
                Actions.showSignInModal({
                    isOpen: true,
                    callback: () => {
                        // After sign-in, refresh basket and wait for completion
                        if (typeof RwdBasketActions?.refreshBasket === 'function') {
                            dispatch(RwdBasketActions.refreshBasket()).then(() => {
                                // Then retry the add operation
                                dispatch(addItemToSaveForLater({ skuId, productId, item, basketType }));
                            });
                        } else {
                            // Fallback If refreshBasket is not available, proceed with add operation
                            dispatch(addItemToSaveForLater({ skuId, productId, item, basketType }));
                        }
                    }
                })
            );

            return null;
        }

        if (!skuId) {
            const error = new Error('Missing skuId for Save For Later');

            return Promise.reject(error);
        }

        // Check if max items limit has been reached
        const itemCount = state.saveForLater?.itemCount || 0;

        if (itemCount >= SAVE_FOR_LATER_MAX_ITEMS) {
            dispatch(
                showGenericErrorModal(null, {
                    message: getText('maxItemsReachedMessage'),
                    title: getText('genericErrorTitle'),
                    buttonText: getText('ok'),
                    buttonWidth: '100%'
                })
            );

            return Promise.resolve({
                status: 'LIMIT_REACHED',
                skuId,
                productId,
                item
            });
        }

        // Check if item already exists in the list to prevent duplicates
        const existingItems = state.saveForLater?.items || Empty.Array;
        const alreadyExists = existingItems.some(existingItem => (existingItem.skuId || existingItem.sku?.skuId) === skuId);

        if (alreadyExists) {
            // Item already in list - return success with ALREADY_SAVED status
            return Promise.resolve({
                status: 'ALREADY_SAVED',
                skuId,
                productId,
                item
            });
        }

        // Optimistic update
        dispatch(addItemOptimistic({ skuId, productId, item }));

        // Ensure list exists, then add item
        return dispatch(ensureSaveForLaterListExists(profileId))
            .then(listId => {
                const payload = {
                    skuId,
                    productId,
                    shoppingListId: listId,
                    shoppingListType: SAVE_FOR_LATER_LIST_TYPE,
                    source: 'basket'
                };

                return dispatch(createSaveForLaterItem(profileId, payload));
            })
            .then(response => {
                // After successful save, delete item from basket
                if (item) {
                    const { BasketType } = basketConstants;
                    const isBopis = basketType === BasketType.BOPIS;

                    return dispatch(
                        RwdBasketActions.deleteItem({
                            sku: item.sku?.sku || item.sku || item,
                            qty: item.qty || 1,
                            isBopis,
                            productId
                        })
                    ).then(() => response);
                }

                return response;
            })
            .then(response => {
                // After successful deletion, set confirmation
                if (item) {
                    dispatch(
                        setBasketConfirmation({
                            skuId,
                            productId,
                            brandName: item.sku?.brandName || item.sku?.sku?.brandName || Empty.String,
                            productName: item.sku?.productName || item.sku?.sku?.productName || Empty.String,
                            basketType,
                            qty: item.qty || 1,
                            sku: item.sku || item
                        })
                    );

                    triggerSaveForLaterLinkEvent({
                        action: SFL_ACTIONS.SAVE,
                        skuId,
                        basketType
                    });
                }

                return {
                    status: 'NEWLY_SAVED',
                    skuId,
                    productId,
                    item,
                    response
                };
            })
            .catch(error => {
                // Remove optimistic update on error
                dispatch(removeItemOptimistic(skuId));

                dispatch(showGenericErrorModal(error));

                throw error;
            });
    };
}

/**
 * Remove an item from Save For Later and refresh the lists
 * @param {Object} params
 * @param {Object} params.sku - SKU object to remove
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @returns {Promise}
 */
function removeItemFromSaveForLater({ sku, userId, listId, context = SFL_CONTEXTS.CAROUSEL }) {
    return dispatch => {
        if (!sku || !userId || !listId) {
            return Promise.resolve();
        }

        const skuId = sku.skuId || sku.sku?.skuId;
        const productId = sku.productId || sku.sku?.productId;

        if (!skuId || !productId) {
            return Promise.resolve();
        }

        return dispatch(
            deleteSaveForLaterItem(userId, {
                skuId,
                productId,
                shoppingListId: listId,
                shoppingListType: SAVE_FOR_LATER_LIST_TYPE
            })
        )
            .then(() => dispatch(getSaveForLaterLists(userId)))
            .then(() => {
                triggerSaveForLaterLinkEvent({
                    action: SFL_ACTIONS.REMOVE,
                    context,
                    skuId
                });
            })
            .catch(error => {
                dispatch(showGenericErrorModal(error));

                Sephora.logger.error('Error removing Save For Later item:', error);
                throw error;
            });
    };
}

/**
 * Close the Save For Later modal
 */
function closeSavedForLaterModal() {
    return dispatch => {
        dispatch(Actions.showSavedForLaterModal({ isOpen: false }));
    };
}

/**
 * Show the Change Method modal for Save For Later flow
 * @param {Object} params
 * @param {Object} params.sku - SKU object
 * @param {number} params.qty - Quantity (default: 1)
 * @param {Function} params.onChangeMethod - Callback when delivery method is selected
 */
function showChangeMethodModalForSaveForLater({ sku, qty = 1, onChangeMethod, context = SFL_CONTEXTS.CAROUSEL }) {
    return dispatch => {
        dispatch(
            Actions.showChangeMethodModal({
                isOpen: true,
                item: { sku, qty },
                itemDeliveryMethod: null,
                onChangeMethod
            })
        );

        const skuId = sku.skuId || sku.sku?.skuId;

        if (skuId) {
            triggerSaveForLaterPageLoad({
                action: SFL_ACTIONS.CHOOSE_METHOD,
                context,
                skuId
            });
        }
    };
}

/**
 * Move an item from Save For Later to basket
 * - Adds item to basket with selected delivery method
 * - Removes item from Save For Later on success
 * - Shows confirmation message
 * - Closes the Change Method modal
 * @param {Object} params
 * @param {string} params.skuId - SKU ID
 * @param {string} params.productId - Product ID
 * @param {number} params.qty - Quantity (default: 1)
 * @param {string} params.deliveryOption - Delivery option (BOPIS, SAMEDAY, or standard)
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @param {Object} params.sku - Full SKU object with brandName, productName, etc.
 * @returns {Promise}
 */
function moveItemToBasketFromSaveForLater({
    skuId, productId, qty = 1, deliveryOption, userId, listId, sku: fullSku
}) {
    return dispatch => {
        const { BasketType } = basketConstants;

        // Determine basket type based on delivery option
        let basketType = BasketType.Standard;

        if (deliveryOption === DELIVERY_METHOD_TYPES.BOPIS) {
            basketType = BasketType.BOPIS;
        } else if (deliveryOption === DELIVERY_METHOD_TYPES.SAMEDAY) {
            basketType = BasketType.SameDay;
        }

        // Build sku object for addToBasket
        const sku = {
            skuId,
            productId
        };

        // Success callback to remove from Save For Later and close modal
        const successCallback = () => {
            if (userId && listId) {
                dispatch(
                    deleteSaveForLaterItem(userId, {
                        skuId,
                        productId,
                        shoppingListId: listId,
                        shoppingListType: SAVE_FOR_LATER_LIST_TYPE
                    })
                )
                    .then(() => dispatch(getSaveForLaterLists(userId)))
                    .then(() => {
                        // Remove moving state after successful move
                        dispatch(removeMovingSku(skuId));

                        triggerSaveForLaterLinkEvent({
                            action: SFL_ACTIONS.MOVE_TO_BASKET,
                            skuId,
                            deliveryOption
                        });

                        // Set confirmation data
                        if (fullSku) {
                            dispatch(
                                setConfirmation({
                                    skuId,
                                    productId,
                                    qty,
                                    deliveryOption,
                                    basketType,
                                    sku: fullSku,
                                    brandName: fullSku.brandName || fullSku.sku?.brandName || Empty.String,
                                    productName: fullSku.productName || fullSku.sku?.productName || Empty.String
                                })
                            );
                        }
                    });
            }

            dispatch(Actions.showChangeMethodModal({ isOpen: false }));
        };

        // Mark this item as moving to show loading state (for modal)
        dispatch(setMovingSku(skuId));

        return dispatch(
            AddToBasketActions.addToBasket(
                sku,
                basketType,
                qty,
                successCallback,
                'save-for-later',
                false,
                {},
                false,
                false,
                '',
                productId,
                false,
                false,
                false,
                null
            )
        ).catch(error => {
            // Remove moving state on error to restore button (for modal)
            dispatch(removeMovingSku(skuId));
            dispatch(Actions.showChangeMethodModal({ isOpen: false }));

            // Show error modal with custom title for limit exceeded errors
            const isLimitExceededError = error?.key && basketUtils.isLimitExceededError(error.key);
            const modalOptions = isLimitExceededError ? { title: getText('limitExceededTitle') } : undefined;

            dispatch(showGenericErrorModal(error, modalOptions));

            Sephora.logger.error('Error adding to basket from save-for-later:', error);

            // Re-throw error so component's catch handler can clean up local state
            return Promise.reject(error);
        });
    };
}

/**
 * Undo adding an item to Save For Later
 * - Removes item from Save For Later
 * - Adds item back to the original basket
 * - Clears confirmation message
 * @param {Object} params
 * @param {string} params.skuId - SKU ID
 * @param {string} params.productId - Product ID
 * @param {number} params.qty - Quantity
 * @param {string} params.basketType - Original basket type
 * @param {Object} params.sku - Full SKU object
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @returns {Promise}
 */
function undoAddToSaveForLater({
    skuId, productId, qty = 1, basketType, sku, userId, listId, context = SFL_CONTEXTS.CAROUSEL
}) {
    return dispatch => {
        if (!sku || !userId || !listId) {
            return Promise.resolve();
        }

        const skuForBasket = {
            skuId,
            productId
        };

        // Success callback to remove from Save for Later after adding to basket
        const successCallback = () => {
            dispatch(
                deleteSaveForLaterItem(userId, {
                    skuId,
                    productId,
                    shoppingListId: listId,
                    shoppingListType: SAVE_FOR_LATER_LIST_TYPE
                })
            )
                .then(() => dispatch(getSaveForLaterLists(userId)))
                .then(() => {
                    triggerSaveForLaterLinkEvent({
                        action: SFL_ACTIONS.UNDO,
                        context,
                        skuId
                    });

                    // Clear confirmation after successful undo
                    dispatch(clearConfirmation());
                });
        };

        // Add back to basket
        return dispatch(
            AddToBasketActions.addToBasket(
                skuForBasket,
                basketType,
                qty,
                successCallback,
                'save-for-later-undo',
                false,
                Empty.Object,
                false,
                false,
                Empty.String,
                productId,
                false,
                false,
                false,
                null
            )
        ).catch(error => {
            dispatch(showGenericErrorModal(error));

            Sephora.logger.error('Error undoing Save for Later:', error);
            throw error;
        });
    };
}

/**
 * Undo moving an item from Save For Later to basket
 * - Removes item from basket
 * - Adds item back to Save For Later
 * - Clears confirmation message
 * @param {Object} params
 * @param {string} params.skuId - SKU ID
 * @param {string} params.productId - Product ID
 * @param {number} params.qty - Quantity that was moved (to be undone)
 * @param {string} params.basketType - Basket type (Standard, BOPIS, SameDay)
 * @param {Object} params.sku - Full SKU object
 * @param {string} params.userId - User profile ID
 * @param {string} params.listId - Shopping list ID
 * @returns {Promise}
 */
function undoMoveToBasket({
    skuId, productId, qty = 1, basketType, sku, userId, listId, context = SFL_CONTEXTS.CAROUSEL
}) {
    return (dispatch, getState) => {
        if (!sku || !userId || !listId) {
            return Promise.resolve();
        }

        const state = getState();
        const basket = state.basket || Empty.Object;

        const { BasketType } = basketConstants;
        const isBopis = basketType === BasketType.BOPIS;
        const isSameDay = basketType === BasketType.SameDay;

        let basketItems = Empty.Array;

        if (isBopis && basket.pickupBasket?.items) {
            basketItems = basket.pickupBasket.items;
        } else if (isSameDay && basket.sameDayBasket?.items) {
            basketItems = basket.sameDayBasket.items;
        } else if (basket.items) {
            basketItems = basket.items;
        }

        // Find the current basket item
        const currentItem = basketItems.find(item => {
            const itemSkuId = item.sku?.skuId || item.skuId;

            return itemSkuId === skuId;
        });

        const currentQty = currentItem?.qty || 0;
        const remainingQty = currentQty - qty;
        let basketUpdatePromise;

        if (remainingQty > 0) {
            // Decrease the basket quantity
            const commerceId = currentItem?.commerceId;

            if (!commerceId) {
                Sephora.logger.warn('Cannot update basket quantity: missing commerceId');

                return Promise.resolve();
            }

            basketUpdatePromise = dispatch(
                RwdBasketActions.updateSkuQuantity({
                    commerceId,
                    newQty: remainingQty,
                    isBopis,
                    sku
                })
            );
        } else {
            // Remove item entirely from basket if no remaining quantity
            basketUpdatePromise = dispatch(
                RwdBasketActions.deleteItem({
                    sku,
                    qty,
                    isBopis,
                    productId
                })
            );
        }

        // Then, add item back to Save For Later
        return basketUpdatePromise
            .then(() => {
                const payload = {
                    skuId,
                    productId,
                    shoppingListId: listId,
                    source: 'basket'
                };

                return dispatch(createSaveForLaterItem(userId, payload));
            })
            .then(() => {
                triggerSaveForLaterLinkEvent({
                    action: SFL_ACTIONS.UNDO,
                    context,
                    skuId
                });
                dispatch(clearConfirmation());

                return Promise.resolve();
            })
            .catch(error => {
                dispatch(showGenericErrorModal(error));

                Sephora.logger.error('Error undoing move to basket:', error);
                throw error;
            });
    };
}

export default {
    // sync
    setLoading,
    updateLists,
    updateItems,
    addItemOptimistic,
    removeItemOptimistic,
    setMovingSku,
    removeMovingSku,
    clearMovingSkus,
    setConfirmation,
    clearConfirmation,
    setBasketConfirmation,
    clearBasketConfirmation,
    // thunks - low-level API wrappers
    getSaveForLaterLists,
    createSaveForLaterList,
    createSaveForLaterItem,
    deleteSaveForLaterItem,
    // thunks - high-level orchestration
    ensureSaveForLaterListExists,
    addItemToSaveForLater,
    removeItemFromSaveForLater,
    undoAddToSaveForLater,
    moveItemToBasketFromSaveForLater,
    undoMoveToBasket,
    closeSavedForLaterModal,
    showChangeMethodModalForSaveForLater
};
